import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { SiteUtilityBar, utilityBarForPath } from '@/components/chrome/SiteUtilityBar';
import { apiAsset, isPlatformHost } from '@/config';
import { darkenHex } from '@/lib/color';
import { LocaleProvider } from '@/lib/i18n';
import { loadPublicSettings, type PublicSettings } from '@/lib/public-settings';

function ensureMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

// Replaces the Next.js root layout: it applied the agency brand colours as CSS
// variables and set the document title. The SPA does the same after mount.
export function RootLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [settings, setSettings] = useState<PublicSettings | null>(null);

  useEffect(() => {
    // Platform hosts show the SaaS landing page: there is no agency to brand.
    if (isPlatformHost()) {
      document.title = 'fleetava — Logiciel de gestion pour agences de location';
      ensureMeta('name', 'description', 'fleetava aide les agences de location à gérer la flotte, les réservations, les contrats signés, la caisse et leur site public.');
      ensureMeta('property', 'og:title', 'fleetava — Logiciel de gestion pour agences de location');
      ensureMeta('property', 'og:description', 'Flotte, réservations, contrats signés, caisse et site public pour les agences de location.');
      ensureMeta('property', 'og:type', 'website');
      return;
    }

    let cancelled = false;
    loadPublicSettings().then((loaded) => {
      if (cancelled) return;
      setSettings(loaded);
      const root = document.documentElement;
      if (loaded.primaryColor) {
        root.style.setProperty('--color-primary', loaded.primaryColor);
        root.style.setProperty('--color-primary-dark', darkenHex(loaded.primaryColor));
      }
      if (loaded.secondaryColor) root.style.setProperty('--color-secondary', loaded.secondaryColor);
      const name = loaded.shortName || loaded.name;
      if (name) document.title = `${name} Car Rental`;
      if (loaded.faviconUrl) {
        let icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
        if (!icon) {
          icon = document.createElement('link');
          icon.rel = 'icon';
          document.head.appendChild(icon);
        }
        icon.href = apiAsset(loaded.faviconUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const platform = isPlatformHost();
  const bar = utilityBarForPath(pathname, platform);

  return (
    <LocaleProvider>
      <div data-fleetava-brand={settings?.name ?? ''}>
        {bar !== 'hidden' && (
          <SiteUtilityBar
            mode={bar}
            name={settings?.shortName || settings?.name || ''}
            logoUrl={settings?.logoUrl ? apiAsset(settings.logoUrl) : ''}
          />
        )}
        <div className={bar === 'hidden' ? undefined : 'pt-14'}>{children}</div>
      </div>
    </LocaleProvider>
  );
}
